/**
 * Browser Polyfills for Coverstories
 * Consolidated file for all browser compatibility polyfills.
 * Add new polyfills here as needed.
 */

/**
 * Polyfill for globalThis (ES2020)
 * Required by: @liveblocks/client v1.2.0
 * Supports: IE11, Chrome <71, Firefox <65, Safari <12.1
 */
export function installGlobalThisPolyfill(): void {
  if (typeof globalThis !== 'undefined') {
    return; // Already available
  }

  // Determine the global object based on environment
  const getGlobalObject = (): any => {
    // Try different global object references
    if (typeof self !== 'undefined') return self;
    if (typeof window !== 'undefined') return window;
    if (typeof global !== 'undefined') return global;
    // Returning null means no known global object (extremely rare)
    return null;
  };

  const globalObject = getGlobalObject();
  
  if (globalObject && !globalObject.globalThis) {
    // Use Object.defineProperty to avoid TypeScript read-only error
    Object.defineProperty(globalObject, 'globalThis', {
      value: globalObject,
      writable: true, // ES2020 specifies globalThis as writable
      enumerable: false,
      configurable: true,
    });
  }
}

/**
 * Polyfill for queueMicrotask
 * Required by: modern framework/runtime internals in older browsers.
 * Supports: Safari <13, older Chromium/Firefox versions.
 */
export function installQueueMicrotaskPolyfill(): void {
  if (typeof queueMicrotask !== 'undefined') {
    return; // Already available
  }

  const target =
    typeof globalThis !== 'undefined'
      ? globalThis
      : typeof window !== 'undefined'
        ? window
        : typeof self !== 'undefined'
          ? self
          : undefined;

  if (!target) {
    return;
  }

  Object.defineProperty(target, 'queueMicrotask', {
    value: (callback: () => void) => Promise.resolve().then(callback),
    writable: true,
    configurable: true,
  });
}

/**
 * Initialize all polyfills
 * Call this at application startup
 */
export function initializePolyfills(): void {
  installGlobalThisPolyfill();
  installQueueMicrotaskPolyfill();
  // Add more polyfill initializers here as needed
}

// Auto-execute polyfills when this module is imported
// Note: This module is imported only in a client component, so this always runs in browser
initializePolyfills();
